import { ZyVersionBadge } from '@/components/tiptap-editor/tiptap-extension/zyversion-extension/zyversion-badge';
import { useEditorPaneBounds, useFullscreenBoundaryContext } from '@/components/tiptap-editor/tiptap-ui/fullscreen-boundary-context';
import { useTiptapEditor } from '@/hooks/use-tiptap-editor';
import { getEditorVersionFilter, getZyVersions, normalizeSelectedZyVersionFilter } from '@/lib/zy-tiptap-utils';
import type { Editor } from '@tiptap/react';
import { useEditorState } from '@tiptap/react';
import { useMemo } from 'react';

const EMPTY_VERSIONS: string[] = [];
const LEGEND_INSET_PX = 16;

interface ZyVersionSelectionLegendProps {
    /** When omitted, uses `EditorContext`. In split view, pass the main editor (not a pane). */
    editor?: Editor | null;
}

export function ZyVersionSelectionLegend({ editor: providedEditor }: ZyVersionSelectionLegendProps) {
    const { editor } = useTiptapEditor(providedEditor);
    const { contentWrapperRef } = useFullscreenBoundaryContext();

    const allVersions =
        useEditorState({
            editor,
            selector: ({ editor: ed }) => (ed ? getZyVersions(ed) : EMPTY_VERSIONS),
        }) ?? EMPTY_VERSIONS;

    const selectedZyVersion = useEditorState({
        editor,
        selector: ({ editor: ed }) => (ed ? getEditorVersionFilter(ed) : null),
    });

    const visibleVersions = useMemo(() => {
        const selection = normalizeSelectedZyVersionFilter(selectedZyVersion ?? null);
        if (selection === null) {
            return allVersions;
        }
        const selected = new Set(selection);
        return allVersions.filter(version => selected.has(version));
    }, [allVersions, selectedZyVersion]);

    const showLegend = Boolean(editor) && visibleVersions.length > 0;
    const bounds = useEditorPaneBounds(contentWrapperRef, showLegend);

    if (!showLegend || !bounds) {
        return null;
    }

    return (
        <div
            className="pointer-events-none fixed z-20 flex -translate-x-full -translate-y-full flex-col items-end gap-1.5"
            style={{
                top: bounds.top + bounds.height - LEGEND_INSET_PX,
                left: bounds.left + bounds.width - LEGEND_INSET_PX,
            }}
            aria-label="Selected versions"
        >
            {visibleVersions.map(version => (
                <ZyVersionBadge
                    key={version}
                    version={version}
                />
            ))}
        </div>
    );
}

export default ZyVersionSelectionLegend;
